import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Table, Alert, Badge } from "reactstrap";
import { toast } from "react-toastify";
import api from "service/api";
import newApi from "service/newApi";
import Loading from "components/Loading";
import "./index.css";

const OPERATOR_LABELS = {
  eq: "igual a",
  ne: "diferente de",
  gt: "maior que",
  gte: "maior ou igual a",
  lt: "menor que",
  lte: "menor ou igual a",
  contains: "contém",
  not_contains: "não contém",
  starts_with: "começa com",
  ends_with: "termina com",
  equals: "igual a",
  not_equals: "diferente de",
  greater: "maior que",
  greater_or_equal: "maior ou igual a",
  less: "menor que",
  less_or_equal: "menor ou igual a",
};

const PipelineOverviewFiltersPreview = ({ executionId }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [filtersData, setFiltersData] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [showingPreview, setShowingPreview] = useState(false);
  const [uniqueEventIds, setUniqueEventIds] = useState([]);
  const [groupsAccordionOpen, setGroupsAccordionOpen] = useState(false);

  const fetchFiltersData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/pipeline/${id}/summary${executionId ? `?executionId=${executionId}` : ""}`,
      );
      const summary = response.data;

      // Filter storage_sources to only include event sources (AUDIO and TEXT), excluding metadata
      const eventSources =
        summary?.storage_sources?.filter(
          (s) => s.type === "AUDIO" || s.type === "TEXT",
        ) || [];

      // Determine origin from the first event source
      const origin = eventSources[0]?.origin || "CLOUD";
      const folderIds = [...new Set(eventSources.map((s) => s.folder_id))];

      // Determine data source type from the first event source (AUDIO or TEXT)
      const storageType = eventSources[0]?.type || "TEXT";
      const dataSourceType = storageType === "AUDIO" ? "AUDIO" : "csv";

      // Fetch event sources to get total events count
      let totalEvents = 0;
      if (folderIds.length > 0) {
        try {
          const requestBody = {
            folderIds: folderIds,
            type: dataSourceType,
          };

          // Add clientId when available
          if (summary?.client_id) {
            requestBody.clientId = summary.client_id;
          }

          if (
            summary?.metadata_filters &&
            summary.metadata_filters.length > 0
          ) {
            requestBody.pipelineId = summary.id;
          }

          const eventsResponse = await newApi.post(
            "admin/v1/events/sources/by-folders?page=1&limit=10000&type=" +
              dataSourceType,
            requestBody,
          );

          // Handle different response formats for AUDIO vs TEXT
          if (dataSourceType === "AUDIO") {
            // For AUDIO: response has { files: [...], total: number }
            totalEvents = eventsResponse?.data?.total || 0;
            const audioFiles = eventsResponse?.data?.files || [];
            // Extract event IDs from file names
            const eventIds = audioFiles
              .map((file) => file.name)
              .filter((name) => name && name !== "No file name found");
            setUniqueEventIds(eventIds);
          } else {
            // For TEXT/CSV: response has { totalUniqueEvents, uniqueEventIds }
            totalEvents = eventsResponse?.data?.totalUniqueEvents || 0;
            setUniqueEventIds(eventsResponse?.data?.uniqueEventIds || []);
          }
        } catch (eventsError) {
          console.error("Error fetching events:", eventsError);
        }
      }

      // Store metadata status
      const hasMetadataFilters = summary?.metadata_is_filter || false;

      // Separate global LIMIT filter from regular filters and filter groups
      const allFilters = summary?.metadata_filters || [];
      const globalLimitFilter = allFilters.find(
        (f) => f.operator === "LIMIT" && f.column === "global",
      );

      // Filter groups are objects with 'conditions' array
      // Legacy filters are objects with 'column', 'operator', 'value' properties
      const filterGroups = allFilters.filter(
        (f) =>
          f.conditions &&
          Array.isArray(f.conditions) &&
          !(f.operator === "LIMIT" && f.column === "global"),
      );

      const regularFilters = allFilters.filter(
        (f) =>
          !f.conditions &&
          !(f.operator === "LIMIT" && f.column === "global") &&
          f.column &&
          f.operator,
      );

      setFiltersData({
        metadataFilters: regularFilters,
        metadataFilterGroups: filterGroups,
        metadataRecordsLimit:
          globalLimitFilter?.limit || summary?.metadata_records_limit,
        appliedFilters: regularFilters.length + filterGroups.length,
        totalRecords: totalEvents,
        folderIds: folderIds,
        clientId: summary?.client_id,
        origin: origin,
        dataSourceType: dataSourceType,
        hasMetadataFilters: hasMetadataFilters,
        eventDistributionColumn: summary?.event_distribution_column || "",
        eventDistributionLimit: summary?.event_distribution_limit || "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar informações de filtros.");
    }
    setLoading(false);
  }, [id, executionId]);

  const getUniqueColumns = () => {
    // Get columns from preview data if available
    if (previewData && previewData.length > 0) {
      const columnsSet = new Set();
      previewData.forEach((record) => {
        Object.keys(record).forEach((key) => {
          // Skip internal metadata fields
          if (!key.startsWith("_")) {
            columnsSet.add(key);
          }
        });
      });
      return Array.from(columnsSet).sort();
    }
    return [];
  };

  const fetchPreviewData = useCallback(async () => {
    if (!filtersData || showingPreview) return;

    setLoading(true);
    setShowingPreview(true);

    try {
      // Check if metadata filters are enabled
      if (!filtersData.hasMetadataFilters) {
        setPreviewData([]);
        setLoading(false);
        toast.info("Nenhum filtro de metadata configurado.");
        return;
      }

      // Build request body for filter-preview API
      const requestBody = {
        folderIds: filtersData.folderIds,
        origin: filtersData.origin,
        page: 1,
        pageSize: 100,
      };

      // Add clientId for LOCAL origin
      const dataSourceBase =
        filtersData.dataSourceType?.toLowerCase() === "audio"
          ? "CLOUD"
          : "LOCAL";
      if (dataSourceBase === "LOCAL" && filtersData.clientId) {
        requestBody.clientId = filtersData.clientId;
      }

      // Add unique event IDs for CLOUD origin
      if (dataSourceBase === "CLOUD" && uniqueEventIds.length > 0) {
        requestBody.uniqueEventIds = uniqueEventIds;
      }

      // Add filter groups if configured
      if (
        filtersData.metadataFilterGroups &&
        filtersData.metadataFilterGroups.length > 0
      ) {
        const validGroups = filtersData.metadataFilterGroups
          .filter((group) => group.conditions && group.conditions.length > 0)
          .map((group) => {
            const validConditions = group.conditions.filter(
              (condition) =>
                condition.column &&
                condition.column.trim() !== "" &&
                condition.operator &&
                condition.operator.trim() !== "" &&
                condition.value &&
                String(condition.value).trim() !== "",
            );

            if (validConditions.length === 0) return null;

            const groupData = {
              id: group.id,
              conditions: validConditions.map((condition) => ({
                column: condition.column.trim(),
                operator: condition.operator.trim(),
                value: String(condition.value).trim(),
              })),
            };

            const limit = parseInt(group.limit);
            if (limit && !isNaN(limit) && limit > 0) {
              groupData.limit = limit;
            }

            return groupData;
          })
          .filter((group) => group !== null);

        if (validGroups.length > 0) {
          requestBody.filterGroups = validGroups;
        }
      } else if (
        filtersData.metadataFilters &&
        filtersData.metadataFilters.length > 0
      ) {
        // Add legacy filters for backward compatibility
        const validFilters = filtersData.metadataFilters
          .filter(
            (filter) =>
              filter.column &&
              filter.column.trim() !== "" &&
              filter.operator &&
              filter.operator.trim() !== "" &&
              filter.value &&
              String(filter.value).trim() !== "",
          )
          .map((filter) => {
            const filterData = {
              column: filter.column.trim(),
              operator: filter.operator.trim(),
              value: String(filter.value).trim(),
            };

            const limit = parseInt(filter.limit);
            if (limit && !isNaN(limit) && limit > 0) {
              filterData.limit = limit;
            }

            return filterData;
          });

        if (validFilters.length > 0) {
          requestBody.legacyFilters = validFilters;
        }
      }

      // Add global limit
      const globalLimit = parseInt(filtersData.metadataRecordsLimit);
      if (globalLimit && !isNaN(globalLimit) && globalLimit > 0) {
        requestBody.globalLimit = globalLimit;
      }

      // Add event distribution configuration
      if (
        filtersData.eventDistributionColumn &&
        filtersData.eventDistributionColumn.trim() !== "" &&
        filtersData.eventDistributionLimit &&
        parseInt(filtersData.eventDistributionLimit) > 0
      ) {
        requestBody.eventDistribution = {
          column: filtersData.eventDistributionColumn.trim(),
          eventsPerValue: parseInt(filtersData.eventDistributionLimit),
        };
      }

      // Call filter-preview API
      const { data } = await newApi.post(
        "admin/v1/events/metadata/filter-preview",
        requestBody,
        {
          timeout: 180000,
        },
      );

      // Set preview data from API response
      setPreviewData(data.records || []);

      toast.success(
        `Preview carregado: ${data.pagination?.totalRecords || data.records?.length || 0} registros encontrados`,
      );
    } catch (error) {
      const errorMessage =
        error.response?.status === 403
          ? "Acesso negado. Você não tem permissão para visualizar estes dados."
          : "Erro ao buscar preview dos resultados filtrados. Tente novamente.";
      toast.error(errorMessage);
      setPreviewData([]);
      setShowingPreview(false);
    }
    setLoading(false);
  }, [filtersData, showingPreview, uniqueEventIds]);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  const renderFilterBadges = () => {
    const hasGroups =
      filtersData?.metadataFilterGroups &&
      filtersData.metadataFilterGroups.length > 0;
    const hasLegacyFilters =
      filtersData?.metadataFilters && filtersData.metadataFilters.length > 0;

    if (!hasGroups && !hasLegacyFilters) {
      return <Alert color="info">Nenhum filtro aplicado</Alert>;
    }

    return (
      <div>
        {/* Group-based filters */}
        {hasGroups && (
          <div className="mb-4">
            <Card className="mb-3">
              <div
                className="card-header bg-gradient-secondary"
                style={{ cursor: "pointer" }}
                onClick={() => setGroupsAccordionOpen(!groupsAccordionOpen)}
              >
                <h5 className="mb-0 d-flex justify-content-between align-items-center">
                  <span>
                    <i
                      className={`ni ${
                        groupsAccordionOpen ? "ni-bold-down" : "ni-bold-right"
                      } mr-2`}
                    ></i>
                    Grupos de Filtros{" "}
                    <Badge color="success" pill className="ml-2">
                      {filtersData.metadataFilterGroups.length}
                    </Badge>
                  </span>
                </h5>
              </div>
              {groupsAccordionOpen && (
                <div className="card-body">
                  {filtersData.metadataFilterGroups.map((group, groupIndex) => {
                    // Filter only valid conditions
                    const validConditions =
                      group.conditions?.filter(
                        (c) => c.column && c.operator && c.value,
                      ) || [];

                    if (validConditions.length === 0) return null;

                    return (
                      <div key={groupIndex} className="mb-4">
                        <h6 className="text-success">
                          <i className="ni ni-collection mr-2"></i>
                          Grupo #{groupIndex + 1}
                          {group.limit && (
                            <Badge color="info" className="ml-2">
                              Limite: {group.limit}
                            </Badge>
                          )}
                        </h6>
                        <div className="table-responsive">
                          <table className="table table-sm align-items-center">
                            <thead className="thead-light">
                              <tr>
                                <th style={{ width: "60px" }}>#</th>
                                <th>Coluna</th>
                                <th>Operador</th>
                                <th>Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {validConditions.map((condition, condIndex) => (
                                <tr key={condIndex}>
                                  <td>
                                    <Badge
                                      color="primary"
                                      pill
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        padding: "4px 8px",
                                      }}
                                    >
                                      #{condIndex + 1}
                                    </Badge>
                                    {condIndex > 0 && (
                                      <Badge
                                        color="success"
                                        pill
                                        className="ml-1"
                                        style={{
                                          fontSize: "10px",
                                          padding: "3px 6px",
                                        }}
                                      >
                                        E
                                      </Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Badge
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        padding: "5px 10px",
                                        background: "#e3f2fd",
                                        color: "#1565c0",
                                        border: "none",
                                      }}
                                    >
                                      {condition.column}
                                    </Badge>
                                  </td>
                                  <td>
                                    <span
                                      style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {OPERATOR_LABELS[condition.operator] ||
                                        condition.operator}
                                    </span>
                                  </td>
                                  <td>
                                    <Badge
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        padding: "5px 10px",
                                        background: "#fff3e0",
                                        color: "#e65100",
                                        border: "none",
                                      }}
                                    >
                                      &quot;{condition.value}&quot;
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {groupIndex <
                          filtersData.metadataFilterGroups.length - 1 && <hr />}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Legacy filters */}
        {hasLegacyFilters && !hasGroups && (
          <div>
            <Alert color="warning" className="mb-3">
              <i className="ni ni-notification-70 mr-2"></i>
              <strong>Modo de compatibilidade:</strong> Você está usando o
              formato antigo de filtros.
            </Alert>
            <div className="table-responsive">
              <table className="table table-sm align-items-center">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>Coluna</th>
                    <th>Operador</th>
                    <th>Valor</th>
                    <th>Limite</th>
                  </tr>
                </thead>
                <tbody>
                  {filtersData.metadataFilters.map((filter, idx) => (
                    <tr key={idx}>
                      <td>
                        <Badge
                          color="primary"
                          pill
                          style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            padding: "4px 8px",
                          }}
                        >
                          #{idx + 1}
                        </Badge>
                      </td>
                      <td>
                        <Badge
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            padding: "5px 10px",
                            background: "#e3f2fd",
                            color: "#1565c0",
                            border: "none",
                          }}
                        >
                          {filter.column}
                        </Badge>
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            fontStyle: "italic",
                          }}
                        >
                          {OPERATOR_LABELS[filter.operator] || filter.operator}
                        </span>
                      </td>
                      <td>
                        <Badge
                          style={{
                            fontSize: "12px",
                            fontWeight: "500",
                            padding: "5px 10px",
                            background: "#fff3e0",
                            color: "#e65100",
                            border: "none",
                          }}
                        >
                          &quot;{filter.value}&quot;
                        </Badge>
                      </td>
                      <td>
                        {filter.limit ? (
                          <Badge
                            style={{
                              fontSize: "11px",
                              padding: "4px 8px",
                              background: "#f3e5f5",
                              color: "#6a1b9a",
                              border: "none",
                            }}
                          >
                            <i className="ni ni-ui-04 mr-1"></i>
                            {filter.limit} registros
                          </Badge>
                        ) : (
                          <span
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            Sem limite
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStatistics = () => {
    if (!filtersData) return null;

    return (
      <>
        <div className="row mb-4">
          <div className="col-md-4">
            <Card className="card-stats">
              <CardBody>
                <div className="row">
                  <div className="col">
                    <span className="h5 font-weight-bold mb-0">
                      {filtersData.totalRecords || 0}
                    </span>
                    <p className="mt-1 mb-0 text-sm">
                      <span className="text-nowrap">Total de registros</span>
                    </p>
                  </div>
                  <div className="col-auto">
                    <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow">
                      <i className="ni ni-chart-bar-32"></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          <div className="col-md-4">
            <Card className="card-stats">
              <CardBody>
                <div className="row">
                  <div className="col">
                    <span className="h5 font-weight-bold mb-0">
                      {filtersData.appliedFilters || 0}
                    </span>
                    <p className="mt-1 mb-0 text-sm">
                      <span className="text-nowrap">Filtros ativos</span>
                    </p>
                  </div>
                  <div className="col-auto">
                    <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow">
                      <i className="ni ni-tag"></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          <div className="col-md-4">
            <Card className="card-stats">
              <CardBody>
                <div className="row">
                  <div className="col">
                    <span className="h5 font-weight-bold mb-0">
                      {filtersData.metadataRecordsLimit || "Sem limite"}
                    </span>
                    <p className="mt-1 mb-0 text-sm">
                      <span className="text-nowrap">Limite de registros</span>
                    </p>
                  </div>
                  <div className="col-auto">
                    <div className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow">
                      <i className="ni ni-ui-04"></i>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
        {filtersData.eventDistributionColumn &&
          filtersData.eventDistributionLimit && (
            <div className="row mb-4">
              <div className="col-12">
                <Card className="card-stats bg-gradient-info">
                  <CardBody>
                    <div className="row">
                      <div className="col">
                        <h5 className="text-white mb-2">
                          <i className="ni ni-chart-pie-35 mr-2"></i>
                          Distribuição de Eventos Configurada
                        </h5>
                        <p className="text-white mb-0">
                          Máximo de{" "}
                          <strong>{filtersData.eventDistributionLimit}</strong>{" "}
                          eventos para cada valor único do campo{" "}
                          <code className="text-white bg-primary px-2 py-1 rounded">
                            {filtersData.eventDistributionColumn}
                          </code>
                        </p>
                      </div>
                      <div className="col-auto">
                        <div className="icon icon-shape bg-white text-info rounded-circle shadow">
                          <i className="ni ni-chart-pie-35"></i>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          )}
      </>
    );
  };

  const renderPreviewTable = () => {
    if (previewData.length === 0) {
      return (
        <Alert color="warning">
          <i className="ni ni-bell-55 mr-2"></i>
          {filtersData.metadataFilters.length === 0 &&
          (!filtersData.metadataFilterGroups ||
            filtersData.metadataFilterGroups.length === 0)
            ? "Nenhum filtro aplicado. Configure filtros para visualizar os resultados."
            : "Nenhum resultado encontrado com os filtros aplicados."}
        </Alert>
      );
    }

    const uniqueColumns = getUniqueColumns();
    const displayColumns = uniqueColumns
      .filter((col) => col !== "EVENT_ID" && col !== "ID" && col !== "event_id")
      .slice(0, 10);

    return (
      <div
        className="table-responsive"
        style={{ maxHeight: "600px", overflowY: "auto" }}
      >
        <Table
          className="align-items-center table-hover table-striped"
          size="sm"
        >
          <thead
            className="thead-light"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "#f6f9fc",
            }}
          >
            <tr>
              <th scope="col" style={{ backgroundColor: "#f6f9fc" }}>
                ID
              </th>
              <th scope="col" style={{ backgroundColor: "#f6f9fc" }}>
                Pasta
              </th>
              {displayColumns.map((col, idx) => (
                <th key={idx} style={{ backgroundColor: "#f6f9fc" }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewData.map((record, idx) => (
              <tr key={idx}>
                <td>
                  <code>{record.EVENT_ID || record.event_id || record.ID}</code>
                </td>
                <td>
                  <small>
                    {record._metadata?.folderName || record._folderName || "-"}
                  </small>
                </td>
                {displayColumns.map((col, colIdx) => (
                  <td key={colIdx}>
                    <small>
                      {record[col] !== undefined && record[col] !== null
                        ? String(record[col])
                        : "-"}
                    </small>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        {previewData.length > 0 && (
          <div className="text-center text-muted mt-2">
            <small>
              Mostrando {previewData.length} registro
              {previewData.length !== 1 ? "s" : ""}
            </small>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="bg-secondary">
      <CardHeader className="bg-white border-0">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Filtros baseados em Metadados</h3>
          {filtersData?.appliedFilters > 0 && (
            <Badge color="info" pill>
              {filtersData.appliedFilters}{" "}
              {filtersData.appliedFilters === 1 ? "filtro" : "filtros"}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardBody className="position-relative">
        {loading && <Loading />}

        {renderStatistics()}

        <div className="mb-4">
          <h4 className="mb-3">Filtros Aplicados</h4>
          {renderFilterBadges()}
        </div>

        <div className="mb-3">
          <h4 className="mb-3">Preview dos Resultados</h4>
          <Alert color="info" className="mb-3">
            <i className="ni ni-bulb-61 mr-2"></i>
            Você está vendo exemplos reais dos registros que serão processados
            com base nos filtros aplicados. Isso ajuda a validar se os filtros
            estão corretos antes da execução da pipeline.
          </Alert>
          {!showingPreview ? (
            <div className="text-center">
              <button
                className="btn btn-outline-primary"
                onClick={fetchPreviewData}
                disabled={loading || !filtersData?.hasMetadataFilters}
              >
                <i className="ni ni-zoom-split-in mr-2"></i>
                Preview dos Resultados
              </button>
              {!filtersData?.hasMetadataFilters && (
                <div className="text-muted mt-2">
                  <small>
                    Filtros de metadata não configurados nesta pipeline
                  </small>
                </div>
              )}
            </div>
          ) : (
            renderPreviewTable()
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default PipelineOverviewFiltersPreview;
